import { Line } from '@visx/shape';

import {
  HORIZONTAL_REF_LINE_LABEL_FONT_SIZE,
  HORIZONTAL_REF_LINE_LABEL_FONT_WEIGHT,
  HORIZONTAL_REF_LINE_LABEL_STROKE_WIDTH,
  HORIZONTAL_REF_LINE_LABEL_X_OFFSET,
  HORIZONTAL_REF_LINE_LABEL_Y_OFFSET,
  MINI_CHART_LABEL_GAP,
  REF_LINE_LABEL_SAFETY_BUFFER_PX,
  SVG_TEXT_CAP_HEIGHT_RATIO,
  VERTICAL_REF_LINE_LABEL_Y_OFFSET,
} from '../../../constants';
import {
  getScaleInput,
  isPointInZoomDomain,
  isStringCategoryInZoomDomain,
  remapYValueIfRight,
} from '../../../utils/scaleHelpers';
import type { HorizontalReferenceLineProps, ReferenceLineProps } from '../../types/ReferenceLineProps';
import { useChartContext } from '../../utils/useChartContext';

import { useReferenceLineSetup } from './useReferenceLineSetup';

/**
 * ReferenceLine component for composable LineChart.
 *
 * Renders vertical or horizontal reference lines using visx Line primitive.
 * Accesses scales from XYChart context to position correctly.
 *
 * Reference line events are displayed within the default tooltip when hovering nearby,
 * alongside the series data. This allows for a unified tooltip experience.
 *
 * @example
 * ```tsx
 * <LineChart>
 *   <ReferenceLine
 *     type="vertical"
 *     point={1704067200000}
 *     label="v2.0 Release"
 *     tooltipName="Variations released"
 *     events={[
 *       { seriesKey: 'control', eventDescription: 'Control started' },
 *       { seriesKey: 'treatment', eventDescription: 'Treatment started' },
 *     ]}
 *   />
 *   <Series ... />
 * </LineChart>
 * ```
 */

export function ReferenceLine(props: ReferenceLineProps) {
  // Only BaseReferenceLineProps fields here — shared by both union members, no cast needed.
  // Horizontal-specific props are destructured after the vertical early-return below.
  const {
    type: typeProp,
    referenceKey,
    point: pointProp,
    label,
    color = 'var(--lp-color-text-ui-primary-base)',
    lineStyle = 'dashed',
    strokeWidth = 1,
    strokeDasharray: strokeDasharrayProp,
  } = props;

  const { referenceLineData, xScaleType, zoomDomain, yScaleConfig, yScaleRightConfig } = useChartContext();

  // If referenceKey is provided, read data from context
  const refLineData = referenceKey ? referenceLineData?.find((d) => d.key === referenceKey) : undefined;

  // Use data from context if available, otherwise use props
  const type = refLineData?.type ?? typeProp;
  const point = refLineData?.point ?? pointProp;

  // Get visx DataContext (no registration needed!)
  const { context } = useReferenceLineSetup();

  // Return early if required data is missing (after calling hooks to satisfy Rules of Hooks)
  if (type === undefined || point === undefined) {
    return null;
  }

  if (!context) {
    return null;
  }

  const { xScale, yScale, margin, width, height } = context;

  if (!xScale || !yScale || !width || !height) {
    return null;
  }

  // For vertical reference lines, check if point is within zoom domain
  if (type === 'vertical' && zoomDomain && xScaleType) {
    if (typeof point === 'string') {
      // String category - explicitly check against zoom domain
      if (!isStringCategoryInZoomDomain(point, zoomDomain)) {
        return null;
      }
    } else if (typeof point === 'number') {
      // Numeric point - check using numeric/string domain comparison
      if (!isPointInZoomDomain(point, xScaleType, zoomDomain)) {
        return null;
      }
    }
  }

  // Convert line style to stroke-dasharray
  const strokeDasharray =
    strokeDasharrayProp ?? (lineStyle === 'dashed' ? '5,5' : lineStyle === 'dotted' ? '2,3' : undefined);

  // xScale/yScale return positions in chart space — margin is already excluded from their output.
  // Band scales require string keys; numeric points on band scales are converted via getScaleInput.
  // scaleInput is only used for xScale (vertical lines); horizontal lines use point directly in yScale.
  let scaleInput: string | number = 0;
  if (type === 'vertical') {
    if (typeof point === 'string') {
      scaleInput = point;
    } else if (xScaleType === 'band') {
      scaleInput = getScaleInput(point, true);
    } else {
      scaleInput = point;
    }
  }

  const scaledX = type === 'vertical' ? xScale(scaleInput) : null;

  // If scaledX is undefined, the point is not in the scale's domain (e.g., outside zoom range)
  // Return null to prevent rendering at position 0
  if (type === 'vertical' && (scaledX === null || scaledX === undefined)) {
    return null;
  }

  let xPos = typeof scaledX === 'number' ? scaledX : 0;

  // Center vertical lines within the band so they align with axis ticks and labels.
  if (type === 'vertical' && xScaleType === 'band' && xScale) {
    const bandwidth =
      typeof (xScale as { bandwidth?: () => number }).bandwidth === 'function'
        ? (xScale as { bandwidth: () => number }).bandwidth()
        : 0;
    if (bandwidth > 0) {
      xPos = xPos + bandwidth / 2;
    }
  }

  const horizontalYAxis = type === 'horizontal' ? ((props as HorizontalReferenceLineProps).yAxis ?? 'left') : 'left';
  const yPointForScale =
    type === 'horizontal' && typeof point === 'number'
      ? remapYValueIfRight(point, horizontalYAxis, yScaleRightConfig?.domain, yScaleConfig?.domain)
      : point;
  const scaledY = type === 'horizontal' ? yScale(yPointForScale) : null;
  const yPos = scaledY !== null ? (typeof scaledY === 'number' ? scaledY : Number(scaledY ?? 0)) : 0;

  // Calculate line coordinates based on type
  if (type === 'vertical') {
    const y1 = margin?.top ?? 0;
    const y2 = height - (margin?.bottom ?? 0);

    return (
      <g>
        <Line
          from={{ x: xPos, y: y1 }}
          to={{ x: xPos, y: y2 }}
          stroke={color}
          strokeWidth={strokeWidth}
          strokeDasharray={strokeDasharray}
          pointerEvents="none"
        />
        {label && (
          <text
            x={xPos}
            y={y1 - VERTICAL_REF_LINE_LABEL_Y_OFFSET}
            fontSize={12}
            fill={color}
            textAnchor="middle"
            fontFamily="var(--lp-font-family-ui)"
          >
            {label}
          </text>
        )}
      </g>
    );
  }

  // Vertical has returned — safe to narrow to HorizontalReferenceLineProps here.
  const {
    labelPosition: labelPositionProp = 'auto',
    labelFontSize = HORIZONTAL_REF_LINE_LABEL_FONT_SIZE,
    labelAlign = 'inside',
    labelColor,
    labelFontWeight = HORIZONTAL_REF_LINE_LABEL_FONT_WEIGHT,
  } = props as HorizontalReferenceLineProps;

  // Horizontal reference line
  const x1 = margin?.left ?? 0;
  const x2 = width - (margin?.right ?? 0);

  const labelYPosition = (() => {
    if (labelPositionProp === 'inline') {
      return yPos + labelFontSize * SVG_TEXT_CAP_HEIGHT_RATIO;
    }

    if (labelPositionProp === 'above') {
      return yPos - HORIZONTAL_REF_LINE_LABEL_Y_OFFSET;
    }

    if (labelPositionProp === 'below') {
      return yPos + HORIZONTAL_REF_LINE_LABEL_Y_OFFSET + labelFontSize;
    }

    const marginTop = margin?.top ?? 0;
    const marginBottom = margin?.bottom ?? 0;
    const availableSpaceAbove = yPos - marginTop;
    const availableSpaceBelow = height - marginBottom - yPos;
    const requiredSpace = labelFontSize + HORIZONTAL_REF_LINE_LABEL_Y_OFFSET + REF_LINE_LABEL_SAFETY_BUFFER_PX;

    if (availableSpaceAbove < requiredSpace && availableSpaceBelow >= requiredSpace) {
      return yPos + HORIZONTAL_REF_LINE_LABEL_Y_OFFSET + labelFontSize;
    }

    return yPos - HORIZONTAL_REF_LINE_LABEL_Y_OFFSET;
  })();

  const labelX = labelAlign === 'outside-left' ? x1 - MINI_CHART_LABEL_GAP : x1 + HORIZONTAL_REF_LINE_LABEL_X_OFFSET;
  const labelTextAnchor = labelAlign === 'outside-left' ? 'end' : 'start';
  const resolvedLabelColor = labelColor ?? color;

  return (
    <g>
      <Line
        from={{ x: x1, y: yPos }}
        to={{ x: x2, y: yPos }}
        stroke={color}
        strokeWidth={strokeWidth}
        strokeDasharray={strokeDasharray}
        pointerEvents="none"
      />
      {label && (
        <text
          x={labelX}
          y={labelYPosition}
          fontSize={labelFontSize}
          fill={resolvedLabelColor}
          textAnchor={labelTextAnchor}
          fontFamily="var(--lp-font-family-ui)"
          stroke="var(--lp-color-bg-ui-primary)"
          strokeWidth={HORIZONTAL_REF_LINE_LABEL_STROKE_WIDTH}
          strokeLinejoin="round"
          paintOrder="stroke"
          fontWeight={labelFontWeight}
        >
          {label}
        </text>
      )}
    </g>
  );
}

ReferenceLine.displayName = 'ReferenceLine';
